import { cn } from "./classNames-DkX30VJU.js";
import { useTheme } from "./ThemeProvider-BCHllN7d.js";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { jsx } from "react/jsx-runtime";
// src/blocks/AppShell/SidebarShell.tsx
/**
* Sidebar geometry: left rail + header + main, with an optional right rail.
*
* - Template area names match the slots' `[grid-area:*]` utilities, so source order does not matter.
* - `headerSpan` ({@link AppShellHeaderSpan}) picks the row template; the columns stay the same.
*/
const SidebarShell = ({ ref, className, headerSpan, children, ...props }) => /* @__PURE__ */ jsx("div", {
	ref,
	"data-app-shell-geometry": "sidebar",
	"data-header-span": headerSpan,
	className: cn("tw:relative tw:grid tw:h-svh tw:w-full tw:bg-background", "tw:grid-cols-[auto_1fr_auto] tw:grid-rows-[auto_1fr]", headerSpan === "full" ? cn("tw:[grid-template-areas:\"header_header_header\"_\"sidebar_main_aside\"]", "tw:[&>[data-app-shell-slot=sidebar]]:[transform:translateZ(0)]", "tw:[&>[data-app-shell-slot=aside]]:[transform:translateZ(0)]", "tw:[&>[data-app-shell-slot=sidebar]]:z-50", "tw:[&>[data-app-shell-slot=aside]]:z-30") : "tw:[grid-template-areas:\"sidebar_header_aside\"_\"sidebar_main_aside\"]", className),
	...props,
	children
});
// src/blocks/AppShell/TopNavShell.tsx
/**
* Top-nav geometry: full-width header, main + aside below.
*
* - No `sidebar` area: that slot is hidden, so one composition works under either brand.
* - No `headerSpan`: the header is already full width.
*/
const TopNavShell = ({ ref, className, children, ...props }) => /* @__PURE__ */ jsx("div", {
	ref,
	"data-app-shell-geometry": "topnav",
	className: cn("tw:grid tw:h-svh tw:w-full tw:bg-background", "tw:grid-cols-[1fr_auto] tw:grid-rows-[auto_1fr]", "tw:[grid-template-areas:\"header_header\"_\"main_aside\"]", "tw:[&>[data-app-shell-slot=sidebar]]:hidden", className),
	...props,
	children
});
// src/blocks/AppShell/AppShell.tsx
/**
* Targets a shell slot from outside the shell.
*
* - Each slot component stamps `data-app-shell-slot` with its own name.
* - Exported so a consumer never hand-types the attribute: renaming it then breaks the
*   build instead of silently matching nothing.
*/
const appShellSlotSelector = (slot) => `[data-app-shell-slot="${slot}"]`;
const Root$1 = ({ ref, className, headerSpan = "content", children, ...props }) => {
	const { currentTheme: brand } = useTheme();
	if (brand.shell === "topnav") return /* @__PURE__ */ jsx(TopNavShell, {
		ref,
		className,
		...props,
		children
	});
	return /* @__PURE__ */ jsx(SidebarShell, {
		ref,
		className,
		headerSpan,
		...props,
		children
	});
};
const Header = ({ ref, className, asChild, flush = false, children, ...props }) => {
	return /* @__PURE__ */ jsx(asChild ? Slot : "header", {
		ref,
		"data-app-shell-slot": "header",
		"data-flush": flush || void 0,
		className: cn("tw:flex", flush ? "tw:items-stretch tw:gap-0" : "tw:items-center tw:gap-3 tw:border-b tw:border-border tw:bg-background tw:px-4", "tw:[grid-area:header]", className),
		...props,
		children
	});
};
const SidebarSlot = ({ ref, className, asChild, children, ...props }) => {
	return /* @__PURE__ */ jsx(asChild ? Slot : "div", {
		ref,
		"data-app-shell-slot": "sidebar",
		className: cn("tw:flex tw:min-h-0 tw:flex-col", "tw:[grid-area:sidebar]", className),
		...props,
		children
	});
};
const Main = ({ ref, className, contentClassName, clampContent = false, asChild, children, ...props }) => {
	return /* @__PURE__ */ jsx(asChild ? Slot : "main", {
		ref,
		"data-app-shell-slot": "main",
		className: cn("tw:flex tw:min-w-0 tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-auto", "tw:[grid-area:main]", className),
		...props,
		children: /* @__PURE__ */ jsx(Slottable, {
			child: children,
			children: (content) => /* @__PURE__ */ jsx("div", {
				className: cn("tw:flex tw:flex-col tw:p-6", clampContent ? "tw:min-h-0 tw:flex-1" : "tw:min-h-full tw:shrink-0", contentClassName),
				children: content
			})
		})
	});
};
const Aside = ({ ref, className, asChild, flush = false, children, ...props }) => {
	return /* @__PURE__ */ jsx(asChild ? Slot : "aside", {
		ref,
		"data-app-shell-slot": "aside",
		"data-flush": flush || void 0,
		className: cn("tw:flex tw:min-h-0 tw:shrink-0 tw:flex-col", flush ? "tw:w-auto tw:overflow-visible" : "tw:w-72 tw:overflow-auto tw:border-l tw:border-border tw:bg-background", "tw:[grid-area:aside]", className),
		...props,
		children
	});
};
/**
* App-shell layout: places the same slots in a sidebar or top-nav geometry per `useTheme().brand.shell`.
*
* - **When to use:** the root layout of a navigated app; host a `PageLayout` in `Main`.
* - **When NOT to use:** standalone screens (sign-in, error pages).
* - **Composition:** inside a `<ThemeProvider>`.
*/
const AppShell = Object.assign(Root$1, {
	/** Above `Main` in the sidebar shell; the full-width top bar in the top-nav shell. */
	Header,
	/** Left rail; hidden by the top-nav shell. */
	Sidebar: SidebarSlot,
	/** Scrolling content outlet with a `tw:p-6` gutter; override it via `contentClassName`. */
	Main,
	/** Optional right rail. Present in both shells. */
	Aside
});
export { AppShell, appShellSlotSelector };

//# sourceMappingURL=AppShell-BUa3U7-e.js.map